import DefaultViewManager from 'epubjs/src/managers/default';
import { extend } from 'epubjs/src/utils/core';
import { EVENTS } from 'epubjs/src/utils/constants';
import VIEW_SLIDING_STATE from '../views/viewslidingstate.js';

class SliderManager extends DefaultViewManager {
  constructor(options) {
    super(options);
    this.name = 'slider';
    this.activeViews = { prev: null, current: null, next: null };
    this.isSliding = false;

    // Touch/Drag Support
    this.draggingDirection = null; // 'next' or 'prev'
    this.dragProgression = 0; // 0 to 1
    this.dragStartX = 0;

    // Animation settings
    this.animationDurationMs = 600;

    this._boundFrogSettingsChanged = this._reloadFrogNeighbors.bind(this);
    window.addEventListener('frog-settings-changed', this._boundFrogSettingsChanged);
  }

  _reloadFrogNeighbors() {
    ['prev', 'next'].forEach((slot) => {
      const view = this.activeViews[slot];
      if (view?._originalContents && view.displayed) {
        view.load(view._originalContents).then(() => {
          if (view.preCenterContent) {
            view.preCenterContent(this.activeViews.current);
          }
        });
      }
    });
  }

  isRightToLeft() {
    return this.settings.direction === 'rtl';
  }

  current() {
    return this.activeViews.current;
  }

  visible() {
    return this.activeViews.current ? [this.activeViews.current] : [];
  }

  render(element, size) {
    const el = typeof element === 'string' ? document.getElementById(element) : element;
    if (!el) return;
    // Pass the resolved element (not the string ID) so Stage.getElement skips the getElementById lookup again
    super.render(el, size);
    Object.assign(this.container.style, {
      overflow: 'hidden',
      position: 'relative',
      width: '100%',
      height: '100%',
    });
  }

  /**
   * Register the direction user is dragging
   * @param direction 'next' or 'prev'
   */
  registerPageDragDirection(direction) {
    if (!this.areAnimatableViewsLoaded(direction)) {
      return;
    }

    if (!this.draggingDirection) {
      this.draggingDirection = direction;
    }
  }

  /**
   * Called when a user releases touch/drag
   */
  onPageDragEnd() {
    // Give a slight delay for swipe events
    setTimeout(() => {
      this.resetDraggedPages();
    }, 100);
  }

  /**
   * Check if animation or drag is in progress
   */
  hasAnimationInProgress() {
    return this.isSliding || this.dragProgression > 0;
  }

  /**
   * Move page based on touch drag position
   */
  moveDraggedPage(event) {
    const touchEvent = event.touches[0];
    const { clientX } = touchEvent;
    this.movePagesByDrag(clientX);
  }

  /**
   * Calculate drag progression from touch X position
   */
  getProgressionByDragX(clientX) {
    const containerWidth = this.container.offsetWidth;

    let dragDistance;
    if (this.draggingDirection === 'next') {
      // Dragging left (to show next page)
      dragDistance = Math.max(0, this.dragStartX - clientX);
    } else {
      // Dragging right (to show prev page)
      dragDistance = Math.max(0, clientX - this.dragStartX);
    }

    return Math.max(0, Math.min(1, dragDistance / containerWidth));
  }

  /**
   * Update view positions based on drag
   */
  movePagesByDrag(clientX) {
    if (!this.draggingDirection) {
      return;
    }
    if (this.isSliding) {
      return;
    }

    const progression = this.getProgressionByDragX(clientX);
    this.dragProgression = progression;

    this.setDragViewStyles(progression);
  }

  /**
   * Apply visual styles during drag
   * Handles RTL mode where next/prev positions are inverted
   */
  setDragViewStyles(progression) {
    const currentView = this.activeViews.current;
    const targetView = this.draggingDirection === 'next' ? this.activeViews.next : this.activeViews.prev;

    if (!currentView || !targetView) {
      return;
    }

    const isRTL = this.isRightToLeft();

    // Disable transitions during drag for immediate feedback
    if (currentView.element) {
      currentView.element.style.transition = 'none';

      // Calculate offset based on direction and RTL mode
      let currentOffset;
      if (this.draggingDirection === 'next') {
        // Going to next: current moves in direction of prev
        currentOffset = isRTL ? progression * 100 : -progression * 100;
      } else {
        // Going to prev: current moves in direction of next
        currentOffset = isRTL ? -progression * 100 : progression * 100;
      }
      currentView.element.style.transform = `translate3d(${currentOffset}%, 0, 0)`;
    }

    if (targetView.element) {
      targetView.element.style.transition = 'none';

      // Calculate offset for target view
      let targetOffset;
      if (this.draggingDirection === 'next') {
        // Next starts at its preloaded position and moves to 0
        const startPos = isRTL ? -100 : 100;
        targetOffset = startPos - (isRTL ? -progression * 100 : progression * 100);
      } else {
        // Prev starts at its preloaded position and moves to 0
        const startPos = isRTL ? 100 : -100;
        targetOffset = startPos + (isRTL ? -progression * 100 : progression * 100);
      }
      targetView.element.style.transform = `translate3d(${targetOffset}%, 0, 0)`;
      targetView.element.style.visibility = 'visible';
    }
  }

  /**
   * Reset dragged pages if user didn't complete the swipe
   */
  resetDraggedPages() {
    if (this.draggingDirection && this.dragProgression > 0) {
      // If user dragged but didn't complete swipe, animate back to original
      if (this.dragProgression < 0.3) {
        // Less than 30% - reset backwards
        this.animateSlideWithProgression(this.draggingDirection, 'backwards');
      } else {
        // More than 30% - complete the slide
        this.animateSlideWithProgression(this.draggingDirection, 'forwards');
      }
    }
  }

  /**
   * Reset drag state
   */
  resetDraggingState() {
    this.dragProgression = 0;
    this.draggingDirection = null;
    this.dragStartX = 0;
  }

  /**
   * Check if views are ready for animation
   */
  areAnimatableViewsLoaded(direction) {
    const targetView = direction === 'next' ? this.activeViews.next : this.activeViews.prev;

    if (!targetView) {
      console.debug(`No ${direction} view available`);
      return false;
    }

    if (!targetView.displayed) {
      console.debug(`${direction} view not fully loaded`);
      return false;
    }

    return true;
  }

  display(section, target) {
    /**
     * `this.views` is missing when `render()` early-returned (container removed
     * during teardown). Resolve instead of throwing, which would crash the queue's
     * rAF callback and stall it (POLYLINO-415).
     */
    if (!this.views) {
      return Promise.resolve();
    }

    const targetHref = section.href;

    if (this.activeViews.next && this.areHrefsEqual(targetHref, this.activeViews.next.section.href)) {
      return this.verifyAndSlide('next');
    }

    if (this.activeViews.prev && this.areHrefsEqual(targetHref, this.activeViews.prev.section.href)) {
      return this.verifyAndSlide('prev');
    }

    if (this.activeViews.current && this.areHrefsEqual(targetHref, this.activeViews.current.section.href)) {
      // Sync location
      if (this.activeViews.current.displayed) {
        this.emit(EVENTS.MANAGERS.ADDED, this.activeViews.current);
      }
      return Promise.resolve(this.activeViews.current);
    }
    // Fallback (Hard Reset)
    return this.hardReset(section, target);
  }

  next() {
    if (this.isSliding) {
      console.debug('Already sliding');
      return Promise.resolve();
    }
    if (!this.activeViews.next) {
      return Promise.resolve();
    }

    return this.verifyAndSlide('next');
  }

  prev() {
    if (this.isSliding) {
      console.debug('Already sliding');
      return Promise.resolve();
    }
    if (!this.activeViews.prev) return Promise.resolve();

    return this.verifyAndSlide('prev');
  }

  /**
   * If the target is loading, wait for it instead of resetting.
   */
  async verifyAndSlide(direction) {
    const targetView = direction === 'next' ? this.activeViews.next : this.activeViews.prev;

    if (!targetView) {
      return Promise.resolve(this.activeViews.current);
    }

    // If the target exists but is loading (not displayed yet)
    if (!targetView.displayed) {
      console.debug(`Target ${direction} loading. Waiting...`);

      const isReady = await new Promise((resolve) => {
        // Wait max 2 seconds for a load to finish
        const timeout = setTimeout(() => {
          console.debug('Load timed out. Hard Resetting.');
          resolve(false);
        }, 2000);

        // Poll for completion
        const checkInterval = setInterval(() => {
          if (targetView.displayed) {
            clearTimeout(timeout);
            clearInterval(checkInterval);
            resolve(true);
          }
        }, 50);
      });

      if (isReady) {
        return this.animateSlide(direction);
      }
      return this.hardReset(targetView.section);
    }

    return this.animateSlide(direction);
  }

  animateSlide(direction) {
    return this.animateSlideWithProgression(direction, 'forwards');
  }

  /**
   * Animate slide with support for forwards/backwards
   * @param direction 'next' or 'prev'
   * @param progressionDirection 'forwards' or 'backwards'
   */
  animateSlideWithProgression(direction, progressionDirection = 'forwards') {
    if (this.isSliding) {
      return Promise.resolve(this.activeViews.current);
    }

    // Check if overriding dragged direction
    if (direction !== this.draggingDirection && this.draggingDirection) {
      // Reset any opposite direction drag
      const oppDirection = this.draggingDirection;
      const oppView = oppDirection === 'next' ? this.activeViews.next : this.activeViews.prev;
      if (oppView?.element) {
        oppView.element.style.transition = 'none';
        oppView.updateState(
          oppDirection === 'next' ? VIEW_SLIDING_STATE.PRELOADED_NEXT : VIEW_SLIDING_STATE.PRELOADED_PREV,
        );
      }
      this.resetDraggingState();
    }

    if (!this.areAnimatableViewsLoaded(direction)) {
      return Promise.resolve(this.activeViews.current);
    }

    this.isSliding = true;

    const currentView = this.activeViews.current;
    const targetView = direction === 'next' ? this.activeViews.next : this.activeViews.prev;

    return new Promise((resolve) => {
      if (!this.rendered) {
        // Book closed during animation
        resolve(this.activeViews.current);
        return;
      }

      // Calculate remaining animation duration
      const durationLeft = this.getRemainingAnimationDuration(progressionDirection);
      const easing = 'cubic-bezier(0.25, 1, 0.5, 1)';

      // Setup transitions
      if (currentView?.element) {
        currentView.element.style.transition = `transform ${durationLeft}ms ${easing}`;
      }
      if (targetView?.element) {
        targetView.element.style.transition = `transform ${durationLeft}ms ${easing}`;
        targetView.element.style.visibility = 'visible';
      }

      // Force reflow
      if (currentView?.element) {
        void currentView.element.offsetHeight;
      }

      // Calculate target positions
      const currentTargetOffset = this.getTargetOffset(direction, progressionDirection, 'current');
      const targetTargetOffset = this.getTargetOffset(direction, progressionDirection, 'target');

      // Apply transforms
      if (currentView?.element) {
        currentView.element.style.transform = `translate3d(${currentTargetOffset}%, 0, 0)`;
      }
      if (targetView?.element) {
        targetView.element.style.transform = `translate3d(${targetTargetOffset}%, 0, 0)`;
      }

      // Cleanup after animation
      setTimeout(() => {
        if (progressionDirection === 'forwards') {
          this.handleAnimationEnd(direction);
        } else {
          // Backwards - reset to original positions
          this.resetToOriginalPositions();
        }
        resolve(this.activeViews.current);
      }, durationLeft);
    });
  }

  /**
   * Calculate target offset for animation
   */
  getTargetOffset(direction, progressionDirection, viewType) {
    const isRTL = this.isRightToLeft();

    if (progressionDirection === 'backwards') {
      // Reset to original positions (respecting RTL)
      if (viewType === 'current') {
        return 0;
      }
      // For target view, return to its preloaded position
      if (direction === 'next') {
        return isRTL ? -100 : 100;
      } else {
        return isRTL ? 100 : -100;
      }
    }

    // Forward animation
    if (viewType === 'current') {
      // Current view slides away in opposite direction
      if (direction === 'next') {
        return isRTL ? 100 : -100;
      } else {
        return isRTL ? -100 : 100;
      }
    }
    // Target view slides to center (0%)
    return 0;
  }

  /**
   * Calculate remaining animation duration based on drag progression
   */
  getRemainingAnimationDuration(progressionDirection) {
    if (progressionDirection === 'forwards') {
      return this.animationDurationMs * (1 - this.dragProgression);
    }
    return this.animationDurationMs * this.dragProgression;
  }

  /**
   * Reset views to original positions after backwards animation
   */
  resetToOriginalPositions() {
    // Freeze transitions
    [this.activeViews.prev, this.activeViews.current, this.activeViews.next].forEach((v) => {
      if (v?.element) v.element.style.transition = 'none';
    });

    // Force reflow
    if (this.activeViews.current?.element) {
      void this.activeViews.current.element.offsetHeight;
    }

    // Reset states
    if (this.activeViews.prev) {
      this.activeViews.prev.updateState(VIEW_SLIDING_STATE.PRELOADED_PREV);
    }
    if (this.activeViews.current) {
      this.activeViews.current.updateState(VIEW_SLIDING_STATE.CURRENT_PAGE);
    }
    if (this.activeViews.next) {
      this.activeViews.next.updateState(VIEW_SLIDING_STATE.PRELOADED_NEXT);
    }

    this.resetDraggingState();
    this.isSliding = false;
  }

  handleAnimationEnd(direction) {
    if (!this.rendered) {
      // Book closed during animation
      return;
    }

    // FREEZE TRANSITIONS (Prevent Jumps during swap)
    [this.activeViews.prev, this.activeViews.current, this.activeViews.next].forEach((v) => {
      if (v && v.element) v.element.style.transition = 'none';
    });
    // Force reflow
    if (this.activeViews.current?.element) {
      void this.activeViews.current.element.offsetHeight;
    }

    // Swap References
    if (direction === 'next') {
      if (this.activeViews.prev) this.destroyView(this.activeViews.prev);
      this.activeViews.prev = this.activeViews.current;
      this.activeViews.current = this.activeViews.next;
      this.activeViews.next = null;
    } else {
      if (this.activeViews.next) this.destroyView(this.activeViews.next);
      this.activeViews.next = this.activeViews.current;
      this.activeViews.current = this.activeViews.prev;
      this.activeViews.prev = null;
    }

    // Update State (Classes) — also re-stamp _slot after the swap
    if (this.activeViews.prev) {
      this.activeViews.prev._slot = 'prev';
      if (this.activeViews.prev.isPreCentered !== undefined) {
        this.activeViews.prev.isPreCentered = false;
      }
      this.activeViews.prev.updateState(VIEW_SLIDING_STATE.PRELOADED_PREV);
    }
    if (this.activeViews.current) {
      this.activeViews.current._slot = 'current';
      this.activeViews.current.updateState(VIEW_SLIDING_STATE.CURRENT_PAGE);
      window._frogActiveStorage = this.activeViews.current.iframe?.contentWindow?.localStorage;
    }
    if (this.activeViews.next) {
      this.activeViews.next._slot = 'next';
      if (this.activeViews.next.isPreCentered !== undefined) {
        this.activeViews.next.isPreCentered = false;
      }
      this.activeViews.next.updateState(VIEW_SLIDING_STATE.PRELOADED_NEXT);
    }

    // Reset drag state
    this.resetDraggingState();
    this.isSliding = false;

    // Preload
    if (this.activeViews.current) {
      this.emit(EVENTS.MANAGERS.ADDED, this.activeViews.current);
      this.preloadNeighbors(this.activeViews.current.section);
    }
  }

  hardReset(section, target) {
    this.isSliding = false;
    this.clear();

    const view = this.createView(section);
    view._slot = 'current';
    this.activeViews.current = view;
    this.views.append(view);

    return view
      .render(this.request)
      .then(() => {
        if (!this.container.contains(view.element)) {
          this.container.appendChild(view.element);
        }
        view.updateState(VIEW_SLIDING_STATE.CURRENT_PAGE);
        return view.display(this.request);
      })
      .then(() => {
        window._frogActiveStorage = view.iframe?.contentWindow?.localStorage;
        this.emit(EVENTS.MANAGERS.ADDED, view);
        this.preloadNeighbors(section);
        return view;
      });
  }

  preloadNeighbors(section) {
    const nextItem = section.next();
    const prevItem = section.prev();

    // PARALLEL PRELOADING
    if (nextItem && !this.activeViews.next) {
      this.loadSideView(nextItem, 'next', VIEW_SLIDING_STATE.PRELOADED_NEXT);
    }

    if (prevItem && !this.activeViews.prev) {
      this.loadSideView(prevItem, 'prev', VIEW_SLIDING_STATE.PRELOADED_PREV);
    }
  }

  loadSideView(section, slotKey, state) {
    if (this.activeViews[slotKey]) {
      this.destroyView(this.activeViews[slotKey]);
    }

    const view = this.createView(section);
    view._slot = slotKey;
    this.activeViews[slotKey] = view;
    this.views.append(view);

    // Set the sliding state BEFORE rendering to prevent default CURRENT_PAGE behavior
    view.slidingState = state;

    view
      .render(this.request)
      .then(() => {
        if (view.element && !this.container.contains(view.element)) {
          this.container.appendChild(view.element);
        }
        view.updateState(state);
        return view.display(this.request);
      })
      .then(() => {
        // Pre-center content while off-screen to avoid alignment jump after animation
        if (view.preCenterContent) {
          view.preCenterContent(this.activeViews.current);
        }
        this.emit('preloaded', view);
      })
      .catch((err) => {
        console.warn(`[SliderManager] Side load failed ${slotKey}`, err);
      });
  }

  createView(section) {
    return new this.View(section, extend(this.viewSettings, { axis: this.settings.axis }));
  }

  destroyView(view) {
    if (!view) return;
    this.views.remove(view);
    if (view.element && view.element.parentNode) {
      view.element.parentNode.removeChild(view.element);
    }
    view.destroy();
  }

  clear() {
    this.activeViews = { prev: null, current: null, next: null };
    super.clear();
  }

  areHrefsEqual(href1, href2) {
    if (!href1 || !href2) return false;
    return decodeURI(href1) === decodeURI(href2);
  }

  /**
   * Check if a view is currently visible (for location tracking)
   * Override from DefaultViewManager
   */
  isVisible(view, _offsetPrev, _offsetNext, _container) {
    if (this.isSliding) {
      // During animation, only consider the target view as visible
      return (
        view === this.activeViews.current ||
        view === (this.draggingDirection === 'next' ? this.activeViews.next : this.activeViews.prev)
      );
    }

    // Static - only current page is visible
    return view === this.activeViews.current;
  }
}

export default SliderManager;
